import {
  LibraryActions,
  type LibraryItem,
  type LibraryKind,
  selectLibraryItem,
} from '@octopus-do/octopus-project-core'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { call, put, select } from 'redux-saga/effects'

import { isConflictError, updateLibraryItem } from '@/services/library'
import { LibrarySagaActions } from '../actions'
import { LibraryNotifications } from '../notifications'
import { ensureLibraryColors } from './ensureLibraryColors'

/**
 * An edit of a library symbol: applied to the store at once, sent with the
 * version the store knows. A 409 means someone else got there first — the
 * list is re-read and the edit is dropped.
 */
export function* updateLibrarySymbol({
  payload: { libraryId, patch },
}: ReturnType<typeof LibrarySagaActions.updateSymbol>) {
  const item: LibraryItem<'symbol'> | undefined = yield select(
    selectLibraryItem('symbol', libraryId)
  )

  if (!item) {
    return
  }

  const next: LibraryItem<'symbol'> = {
    ...item,
    data: { ...item.data, ...patch },
  }

  yield put(LibraryActions.upsert({ kind: 'symbol', items: [next] }))

  const workspaceId: string = yield select(
    migrationProjectSelector.getLibraryWorkspaceParam
  )

  try {
    const saved: LibraryItem<LibraryKind> = yield call(
      updateLibraryItem,
      workspaceId,
      'symbol',
      libraryId,
      { data: next.data, version: item.version }
    )

    yield put(
      LibraryActions.upsert({
        kind: 'symbol',
        items: [saved as LibraryItem<'symbol'>],
      })
    )

    yield* ensureLibraryColors()
  } catch (error) {
    console.error('Failed to update the library symbol', error)

    if (isConflictError(error)) {
      yield put(LibraryNotifications.conflict)
    } else {
      yield put(LibraryNotifications.failed('Failed to update the symbol'))
    }

    yield put(LibrarySagaActions.fetch({ kind: 'symbol' }))
  }
}
